import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Star, Zap, Calendar, X, Volume2, VolumeX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PHOTO = "https://images.unsplash.com/photo-1629121699638-976764fb4c8c?w=800&h=1200&fit=crop";

const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function DiscoverySwipe() {
  const navigate = useNavigate();
  const [listeners, setListeners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    fetchListeners();
  }, []);

  useEffect(() => {
    // Play video when index changes
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex]);

  const fetchListeners = async () => {
    try {
      const response = await axios.get(`${API}/listeners`);
      setListeners(response.data);
    } catch (error) {
      console.error("Error fetching listeners:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwipeUp = () => {
    if (currentIndex < listeners.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleSwipeDown = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleWheel = (e) => {
    if (e.deltaY > 0) {
      handleSwipeUp();
    } else {
      handleSwipeDown();
    }
  };

  const handleBook = (username) => {
    navigate(`/l/${username}`);
  };

  const toggleMute = () => {
    setMuted(!muted);
    if (videoRef.current) {
      videoRef.current.muted = !muted;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (listeners.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-white">
        <div className="text-center p-8">
          <p className="text-lg">No hay listeners disponibles</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => navigate("/explorar")}
          >
            Volver a explorar
          </Button>
        </div>
      </div>
    );
  }

  const listener = listeners[currentIndex];

  return (
    <div 
      className="fixed inset-0 bg-black overflow-hidden"
      onWheel={handleWheel}
    >
      {/* Close button */}
      <button 
        className="absolute top-4 right-4 z-50 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white"
        onClick={() => navigate("/explorar")}
      >
        <X className="w-5 h-5" />
      </button>

      {/* Progress indicators */}
      <div className="absolute top-4 left-4 right-16 z-50 flex gap-1">
        {listeners.map((_, i) => (
          <div 
            key={i}
            className={`h-1 flex-1 rounded-full transition-all ${
              i === currentIndex ? "bg-white" : "bg-white/30"
            }`}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative h-full w-full">
        {/* Video/Image background */}
        {listener.video_url ? (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            src={getFullUrl(listener.video_url)}
            loop
            muted={muted}
            playsInline
            autoPlay
          />
        ) : (
          <img
            src={getFullUrl(listener.photo_url) || DEFAULT_PHOTO}
            alt={listener.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

        {/* Mute button (only if video) */}
        {listener.video_url && (
          <button
            className="absolute top-20 right-4 z-50 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white"
            onClick={toggleMute}
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        )}

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 pb-24 z-10">
          {/* Online badge */}
          {listener.is_online && listener.availability_mode === "instant" && (
            <div className="inline-flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-3">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              Disponible ahora
            </div>
          )}

          {/* Name and rating */}
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-3xl font-serif font-bold text-white">
              {listener.name}
            </h2>
            {listener.average_rating && (
              <div className="flex items-center gap-1 bg-white/20 backdrop-blur px-2 py-1 rounded-full">
                <Star className="w-4 h-4 text-amber-400 fill-current" />
                <span className="text-sm text-white font-medium">{listener.average_rating}</span>
              </div>
            )}
          </div>

          {/* Bio */}
          {listener.bio && (
            <p className="text-white/90 text-base line-clamp-3 mb-4">
              {listener.bio}
            </p>
          )}

          {/* Price */}
          <p className="text-white/70 text-sm mb-4">
            ${listener.price_per_session} MXN por sesión
          </p>

          {/* CTA Button */}
          <Button 
            className="w-full py-6 text-lg bg-primary hover:bg-primary/90"
            onClick={() => handleBook(listener.username)}
            data-testid="book-session-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Agendar sesión
          </Button>
        </div>

        {/* Swipe instructions */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center">
          <p className="text-white/50 text-xs">Desliza arriba para ver más</p>
        </div>

        {/* Navigation arrows (for desktop) */}
        <div className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 flex-col gap-4 z-20">
          <button
            className={`w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white ${
              currentIndex === 0 ? "opacity-30" : "hover:bg-white/20"
            }`}
            onClick={handleSwipeDown}
            disabled={currentIndex === 0}
          >
            ↑
          </button>
          <button
            className={`w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white ${
              currentIndex === listeners.length - 1 ? "opacity-30" : "hover:bg-white/20"
            }`}
            onClick={handleSwipeUp}
            disabled={currentIndex === listeners.length - 1}
          >
            ↓
          </button>
        </div>
      </div>
    </div>
  );
}
